import {
  Button,
  FormGridItem,
  InputLabel,
} from '@ace-clublabs/shared-component-library';
import {
  ContactForm,
  ContactFormBody,
  ContactFormGrid,
  ContactFormHeader,
} from '@/components/shared/ContactForm/ContactFormLayout';
import {
  CommunicationPreferenceField,
  EmailField,
  EmailMessageField,
  ExpectedTravelTimeField,
  FirstNameField,
  LastNameField,
  PhoneNumberField,
  TypeOfTripField,
  ZipCodeField,
} from '@/components/shared/ContactForm/fields';
import TermsAndConditions from '@/components/shared/TermsAndConditions';
import type { Destination } from '@/features/destinations/utils/Destination';
import RSSearchBox from '@/features/relevanceSearch/components/RSSearchBox';
import { useRSSearchBox } from '@/features/relevanceSearch/hooks/useRSSearchBox';
import useContactInfoPrefill from '@/hooks/useContactInfoPrefill';
import useAppointmentStore from '@/store/AppointmentStore';
import { FlowType } from '@/types/FlowType';

export interface HandleFormSubmitData {
  selectedDestination: Destination;
  searchedDestination?: string;
  userSearch?: string;
}

interface EmailInquiryFormProps {
  onSubmit: (data: HandleFormSubmitData) => void;
}

const EmailInquiryForm: React.FC<EmailInquiryFormProps> = ({ onSubmit }) => {
  const formValues = useAppointmentStore((state) => state.contactInfo);
  const updateField = useAppointmentStore((state) => state.patchContactInfo);
  const setSelectedDestination = useAppointmentStore(
    (state) => state.setSelectedDestination,
  );
  const setUserSearch = useAppointmentStore((state) => state.setUserSearch);
  const setSearchedDestination = useAppointmentStore(
    (state) => state.setSearchedDestination,
  );

  const { searchBoxProps, resolveDestination } = useRSSearchBox({
    flow: FlowType.EMAIL,
    formVariant: true,
  });

  useContactInfoPrefill({
    formValues,
    updateField,
  });

  const handleSubmit = () => {
    const resolvedDestination = resolveDestination();

    if (!resolvedDestination?.selectedDestination) return;

    const { selectedDestination, searchedDestination, userSearch } =
      resolvedDestination;

    setUserSearch(userSearch ?? '');
    setSearchedDestination(searchedDestination);
    setSelectedDestination(selectedDestination);

    onSubmit({
      selectedDestination,
      searchedDestination,
      userSearch,
    });
  };

  return (
    <ContactForm onSubmit={handleSubmit}>
      <ContactFormBody>
        <ContactFormHeader header='How should we contact you?' />
        <ContactFormGrid>
          <FirstNameField
            value={formValues.firstName}
            onChange={(v) => updateField({ firstName: v })}
            required
          />
          <LastNameField
            value={formValues.lastName}
            onChange={(v) => updateField({ lastName: v })}
            required
          />
          <ZipCodeField
            value={formValues.zipCode}
            onChange={(v) => updateField({ zipCode: v })}
            required
          />
          <CommunicationPreferenceField
            value={formValues.communicationPreference}
            onChange={(v) =>
              updateField({
                communicationPreference: v,
              })
            }
            required
          />
          <PhoneNumberField
            value={formValues.phoneNumber}
            onChange={(v) => updateField({ phoneNumber: v })}
            required
          />
          <EmailField
            value={formValues.email}
            onChange={(v) => updateField({ email: v })}
            required
          />
          <ExpectedTravelTimeField
            value={formValues.expectedTravelTime}
            onChange={(v) => updateField({ expectedTravelTime: v })}
          />
          <TypeOfTripField
            value={formValues.typeOfTrip}
            onChange={(v) => updateField({ typeOfTrip: v })}
            required
          />
          <FormGridItem size={12}>
            <InputLabel>Select a destination*</InputLabel>
            <RSSearchBox {...searchBoxProps} formVariant />
          </FormGridItem>
          <EmailMessageField
            value={formValues.emailMessage}
            onChange={(v) => updateField({ emailMessage: v })}
            required
          />
        </ContactFormGrid>
        <TermsAndConditions buttonLabel='Submit' />
        <Button label='Submit' type='submit' sx={{ mt: 2 }} fullWidth />
      </ContactFormBody>
    </ContactForm>
  );
};

export default EmailInquiryForm;
